import { useQuery } from '@tanstack/react-query'
import {
  ArrowUpRight,
  CalendarDays,
  ChevronDown,
  FileText,
  GraduationCap,
  HeartPulse,
  KeyRound,
  Mail,
  Pencil,
  Phone,
  Shuffle,
  UserCheck,
  UsersRound,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGuardians } from '@/features/auth/guardians'
import { Link, useParams } from 'react-router'
import { ApiError } from '@/api/errors'
import { ErrorState, LoadingState, NotFoundState } from '@/components/feedback/States'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { DescriptionList } from '@/components/ui/DescriptionList'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { Breadcrumbs } from '@/components/ui/PageHeader'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { Can } from '@/features/auth/guards'
import { useAuth } from '@/features/auth/useAuth'
import { studentQuery } from '@/features/students/api'
import { PortalAccountDialog, StatusDialog } from '@/features/students/components/AccountDialogs'
import { DocumentsPanel } from '@/features/students/components/DocumentsPanel'
import { ChangeSectionDialog, PromoteDialog } from '@/features/students/components/PlacementDialogs'
import { studentStatusTone } from '@/features/students/status'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'
import { ageFrom, formatDate, formatDateTime, humanize } from '@/utils/format'
import { sectionLabel } from '@/features/academic/sectionLabel'

type DialogName = 'promote' | 'section' | 'status' | 'account' | null

export function StudentDetailPage() {
  const { t } = useTranslation('students')
  const withGuardians = useGuardians()
  const id = Number(useParams().id)
  const { data: s, isPending, error, refetch } = useQuery(studentQuery(id))
  const { can } = useAuth()
  const [dialog, setDialog] = useState<DialogName>(null)
  useDocumentTitle(s?.summary.fullName ?? 'Student')

  if (isPending) return <LoadingState />
  if (error)
    return error instanceof ApiError && error.status === 404 ? (
      <NotFoundState resource="student" homeHref="/admin/students" />
    ) : (
      <ErrorState onRetry={() => void refetch()} />
    )

  const sum = s.summary
  const current = sum.currentEnrollment
  const age = ageFrom(sum.dateOfBirth)
  const close = (open: boolean) => (open ? undefined : setDialog(null))

  return (
    <>
      <Breadcrumbs
        homeHref="/admin"
        items={[{ label: t('Students'), to: '/admin/students' }, { label: sum.fullName }]}
      />

      {/* Profile hero */}
      <Card className="mt-3 overflow-hidden">
        <div className="bg-brand-gradient h-24 sm:h-28" aria-hidden="true" />
        <div className="flex flex-col gap-4 px-5 pb-5 sm:flex-row sm:items-end sm:px-6">
          <Avatar
            name={sum.fullName}
            src={sum.photoUrl}
            size="xl"
            className="-mt-10 ring-4 ring-surface sm:-mt-12"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold sm:text-2xl">{sum.fullName}</h1>
              <Badge tone={studentStatusTone[sum.status]} dot>
                {humanize(sum.status)}
              </Badge>
            </div>
            <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted">
              <span className="font-mono text-xs">{sum.studentCode}</span>
              <span>{t('Adm. {{number}}', { number: sum.admissionNumber })}</span>
              {current ? (
                <span className="inline-flex items-center gap-1">
                  <GraduationCap className="size-4" aria-hidden="true" />{' '}
                  {sectionLabel(
                    current.className,
                    current.sectionName ? t('Section {{name}}', { name: current.sectionName }) : '',
                    ' · ',
                  )}
                  {current.rollNumber ? ` · ${t('Roll {{number}}', { number: current.rollNumber })}` : ''}
                </span>
              ) : null}
            </p>
          </div>
          <Can permission="students.manage">
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="secondary" leftIcon={<Pencil />}>
                <Link to={`/admin/students/${id}/edit`}>{t('Edit')}</Link>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button rightIcon={<ChevronDown />}>{t('Actions')}</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    icon={<ArrowUpRight />}
                    onSelect={() => setDialog('promote')}
                    disabled={sum.status !== 'active' || !current}
                  >
                    {t('Promote to next year')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    icon={<Shuffle />}
                    onSelect={() => setDialog('section')}
                    disabled={!current}
                  >
                    {t('Change section / roll')}
                  </DropdownMenuItem>
                  <DropdownMenuItem icon={<UserCheck />} onSelect={() => setDialog('status')}>
                    {t('Change status')}
                  </DropdownMenuItem>
                  {can('users.manage') ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem icon={<KeyRound />} onSelect={() => setDialog('account')}>
                        {t('Family-portal login')}
                      </DropdownMenuItem>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </Can>
        </div>
      </Card>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <Card>
          <CardBody>
            <Tabs defaultValue="overview">
              <TabsList aria-label={t('Student record sections')}>
                <TabsTrigger value="overview" icon={<FileText />}>
                  {t('Overview')}
                </TabsTrigger>
                {withGuardians ? (
                  <TabsTrigger value="guardians" icon={<UsersRound />} count={s.guardians.length}>
                    {t('Guardians')}
                  </TabsTrigger>
                ) : null}
                <TabsTrigger value="enrollment" icon={<CalendarDays />} count={s.enrollments.length}>
                  {t('Enrollment')}
                </TabsTrigger>
                <TabsTrigger value="documents" icon={<FileText />} count={s.documents.length}>
                  {t('Documents')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-8">
                <section aria-labelledby="personal">
                  <h2 id="personal" className="mb-4 text-sm font-semibold">
                    {t('Personal details')}
                  </h2>
                  <DescriptionList
                    items={[
                      { label: t('Gender'), value: humanize(sum.gender) },
                      {
                        label: t('Date of birth'),
                        value: sum.dateOfBirth
                          ? `${formatDate(sum.dateOfBirth)}${age !== null ? ` (${t('{{count}} yrs', { count: age })})` : ''}`
                          : '—',
                      },
                      { label: t('Blood group'), value: s.bloodGroup ?? '—' },
                      { label: t('Nationality'), value: s.nationality ?? '—' },
                      { label: t('Religion'), value: s.religion ?? '—' },
                      { label: t('Admitted on'), value: formatDate(sum.admissionDate) },
                    ]}
                  />
                </section>
                <section aria-labelledby="contact">
                  <h2 id="contact" className="mb-4 text-sm font-semibold">
                    {t('Contact')}
                  </h2>
                  <DescriptionList
                    items={[
                      { label: t('Email'), value: s.email ?? '—' },
                      { label: t('Phone'), value: sum.phone ?? '—' },
                      {
                        label: t('Address'),
                        value: s.address ? <span className="whitespace-pre-line">{s.address}</span> : '—',
                        span: 2,
                      },
                    ]}
                  />
                </section>
                <section
                  aria-labelledby="health"
                  className="rounded-control border border-line bg-surface-muted p-4"
                >
                  <h2 id="health" className="flex items-center gap-2 text-sm font-semibold">
                    <HeartPulse className="size-4 text-danger-500" aria-hidden="true" /> {t('Health notes')}
                  </h2>
                  <p className="mt-2 text-sm whitespace-pre-line text-ink-soft">
                    {s.medicalNotes ?? t('No medical notes recorded.')}
                  </p>
                </section>
              </TabsContent>

              <TabsContent value="guardians">
                {s.guardians.length === 0 ? (
                  <p className="text-sm text-muted">{t('No guardians recorded.')}</p>
                ) : (
                  <ul className="grid gap-4 sm:grid-cols-2">
                    {s.guardians.map((g) => (
                      <li
                        key={g.id}
                        className={cn(
                          'rounded-card border p-4',
                          g.isPrimary ? 'border-brand-200 bg-brand-50/40' : 'border-line',
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <Avatar name={g.name} />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-ink">{g.name}</p>
                            <p className="text-xs text-muted">
                              {humanize(g.relation)}
                              {g.occupation ? ` · ${g.occupation}` : ''}
                            </p>
                          </div>
                          {g.isPrimary ? (
                            <Badge tone="brand" size="sm">
                              {t('Primary')}
                            </Badge>
                          ) : null}
                        </div>
                        <div className="mt-4 space-y-1.5 text-sm">
                          {g.phone ? (
                            <a
                              href={`tel:${g.phone}`}
                              className="flex items-center gap-2 text-ink-soft hover:text-brand-700"
                            >
                              <Phone className="size-4 text-subtle" aria-hidden="true" /> {g.phone}
                            </a>
                          ) : null}
                          {g.email ? (
                            <a
                              href={`mailto:${g.email}`}
                              className="flex items-center gap-2 break-all text-ink-soft hover:text-brand-700"
                            >
                              <Mail className="size-4 text-subtle" aria-hidden="true" /> {g.email}
                            </a>
                          ) : null}
                        </div>
                        {g.hasPortalAccess ? (
                          <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-success-700">
                            <KeyRound className="size-3.5" aria-hidden="true" />{' '}
                            {t('Has family-portal access')}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </TabsContent>

              <TabsContent value="enrollment">
                <ol className="relative space-y-6 border-l border-line pl-6">
                  {s.enrollments.map((e) => (
                    <li key={e.id} className="relative">
                      <span
                        className={cn(
                          'absolute top-1 -left-[1.9rem] size-3 rounded-full ring-4 ring-surface',
                          e.isCurrent ? 'bg-brand-600' : 'bg-line-strong',
                        )}
                        aria-hidden="true"
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-ink">{e.academicYear}</p>
                        <Badge tone={e.isCurrent ? 'brand' : 'neutral'} size="sm">
                          {e.isCurrent ? t('Current') : humanize(e.status)}
                        </Badge>
                      </div>
                      <p className="mt-0.5 text-sm text-ink-soft">
                        {sectionLabel(
                          e.className,
                          e.sectionName ? t('Section {{name}}', { name: e.sectionName }) : '',
                          ' · ',
                        )}
                        {e.rollNumber ? ` · ${t('Roll {{number}}', { number: e.rollNumber })}` : ''}
                      </p>
                      <p className="mt-0.5 text-xs text-muted">
                        {t('Enrolled {{date}}', { date: formatDateTime(e.enrolledAt) })}
                        {e.endedAt ? ` · ${t('ended {{date}}', { date: formatDateTime(e.endedAt) })}` : ''}
                      </p>
                    </li>
                  ))}
                </ol>
              </TabsContent>

              <TabsContent value="documents">
                <DocumentsPanel studentId={id} documents={s.documents} />
              </TabsContent>
            </Tabs>
          </CardBody>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader title={t('Current placement')} icon={<GraduationCap />} />
            <CardBody>
              {current ? (
                <DescriptionList
                  className="sm:grid-cols-1"
                  items={[
                    { label: t('Academic year'), value: current.academicYear },
                    {
                      label: t('Class & section'),
                      value: sectionLabel(current.className, current.sectionName, ' · '),
                    },
                    { label: t('Roll number'), value: current.rollNumber ?? '—' },
                  ]}
                />
              ) : (
                <p className="text-sm text-muted">{t('No open enrollment.')}</p>
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title={t('Portal access')} icon={<KeyRound />} />
            <CardBody className="text-sm">
              {s.account ? (
                <p className="text-ink-soft">
                  {t('Student signs in as')} <span className="font-medium text-ink">{s.account.email}</span>
                  {!s.account.isActive ? (
                    <Badge tone="danger" size="sm" className="ml-2">
                      {t('Disabled')}
                    </Badge>
                  ) : null}
                </p>
              ) : (
                <p className="text-muted">{t('The student has no portal login.')}</p>
              )}
              {withGuardians ? (
                <p className="mt-2 text-muted">
                  {t('{{count}} guardian login(s).', {
                    count: s.guardians.filter((g) => g.hasPortalAccess).length,
                  })}
                </p>
              ) : null}
            </CardBody>
          </Card>
          <p className="px-1 text-xs text-subtle">
            {t('Last updated {{date}}', { date: formatDateTime(s.updatedAt) })}
          </p>
        </div>
      </div>

      {/* Mounted only while open so each dialog starts from fresh state. */}
      {dialog === 'promote' ? <PromoteDialog student={s} open onOpenChange={close} /> : null}
      {dialog === 'section' ? <ChangeSectionDialog student={s} open onOpenChange={close} /> : null}
      {dialog === 'status' ? <StatusDialog student={s} open onOpenChange={close} /> : null}
      {dialog === 'account' ? <PortalAccountDialog student={s} open onOpenChange={close} /> : null}
    </>
  )
}
